/**
 * anonCredentialBackup.js — multi-storage anonymous-uid backup
 *
 * The Firebase Auth web SDK keeps its anonymous user refresh token in a
 * single IndexedDB database. On iOS Safari that database can be wiped by
 * ITP after 7 days of no interaction with the origin, and on in-app
 * browsers it's often isolated/cleared per app launch. When that happens
 * the next visit signs in as a brand-new anonymous uid and the guest
 * appears to have "lost their account".
 *
 * This module is a stop-gap: it mirrors the uid (and refresh token, where
 * available) to *every* persistent storage surface a browser exposes, so
 * the chance of at least ONE surviving across a wipe is much higher than
 * relying on Firebase's IndexedDB alone:
 *
 *   • localStorage
 *   • document.cookie (max-age 400d — Chrome's hard cap)
 *   • a SEPARATE IndexedDB database (not the one Firebase uses)
 *   • Cache Storage (controlled by the Service Worker scope)
 *
 * Recovery is partial: true client-side re-auth as a specific historical
 * anonymous uid isn't possible without backend support (Firebase Auth has
 * no public API for it). What this module CAN do is detect on next visit
 * that the device used to have a different anonymous identity, so the app
 * can either show a soft "we noticed you've been here before — link your
 * phone to recover" prompt, or surface the previous uid for server-side
 * migration via a future Cloud Function.
 *
 * See ANONYMOUS_USER_PORTABILITY.md for the full design.
 */

const LS_KEY = 'muuzbox_anon_backup_v1';
const COOKIE_NAME = 'muuzbox_anon_uid';
const COOKIE_MAX_AGE_SEC = 400 * 24 * 60 * 60; // 400 days, Chrome's cap
const IDB_NAME = 'muuzbox-anon-backup';
const IDB_STORE = 'creds';
const IDB_KEY = 'current';
const CACHE_NAME = 'muuzbox-anon-backup-v1';
const CACHE_REQUEST_URL = '/__muuzbox_anon_creds__';

function _serialise(user) {
  if (!user || !user.uid) return null;
  return {
    uid: user.uid,
    refreshToken: user.refreshToken || null,
    displayName: user.displayName || '',
    photoURL: user.photoURL || '',
    savedAt: Date.now(),
  };
}

function _writeLocalStorage(payload) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch (_) {}
}

function _writeCookie(payload) {
  // Only the uid goes in the cookie — refresh tokens are too long for
  // many cookie limits and there's no point in restoring them from a
  // cookie when the Firebase SDK won't accept them anyway.
  try {
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(payload.uid)}; ` +
      `max-age=${COOKIE_MAX_AGE_SEC}; path=/; SameSite=Lax`;
  } catch (_) {}
}

function _readCookie() {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`));
    if (!m) return null;
    return { uid: decodeURIComponent(m[1]), savedAt: 0, source: 'cookie' };
  } catch (_) { return null; }
}

function _idbOpen() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('no idb'));
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      try { req.result.createObjectStore(IDB_STORE); } catch (_) {}
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('idb blocked'));
  });
}

async function _writeIdb(payload) {
  try {
    const db = await _idbOpen();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(payload, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (_) {}
}

async function _readIdb() {
  try {
    const db = await _idbOpen();
    const out = await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return out;
  } catch (_) { return null; }
}

async function _clearIdb() {
  try {
    const db = await _idbOpen();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (_) {}
}

async function _writeCache(payload) {
  try {
    if (typeof caches === 'undefined') return;
    const cache = await caches.open(CACHE_NAME);
    await cache.put(
      new Request(CACHE_REQUEST_URL),
      new Response(JSON.stringify(payload), {
        headers: { 'Content-Type': 'application/json' },
      })
    );
  } catch (_) {}
}

async function _readCache() {
  try {
    if (typeof caches === 'undefined') return null;
    const cache = await caches.open(CACHE_NAME);
    const resp = await cache.match(new Request(CACHE_REQUEST_URL));
    if (!resp) return null;
    return await resp.json();
  } catch (_) { return null; }
}

/**
 * Persist the anonymous user across every storage surface available.
 * Called from Auth.js's signInAnon on a fresh sign-in, AND from the
 * onAuthStateChanged listener so we re-save on every page load (refresh
 * token rotates on each session — keeping all stores up to date matters).
 */
export async function saveAnonCredentials(user) {
  const payload = _serialise(user);
  if (!payload) return;
  _writeLocalStorage(payload);
  _writeCookie(payload);
  // Don't await IDB/Cache — they should be fire-and-forget. Errors are
  // swallowed inside each writer.
  _writeIdb(payload);
  _writeCache(payload);
}

/**
 * Read whatever survived. Picks the most-recently-saved payload across
 * all four storage surfaces. Returns null if every surface is empty.
 */
export async function readAnonCredentials() {
  const candidates = [];

  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) candidates.push({ ...JSON.parse(raw), source: 'localStorage' });
  } catch (_) {}

  const fromCookie = _readCookie();
  if (fromCookie) candidates.push(fromCookie);

  const fromIdb = await _readIdb();
  if (fromIdb) candidates.push({ ...fromIdb, source: 'indexeddb' });

  const fromCache = await _readCache();
  if (fromCache) candidates.push({ ...fromCache, source: 'cacheStorage' });

  if (candidates.length === 0) return null;

  // Cookie-only payloads have savedAt=0 and only the uid — they're a
  // fallback when richer surfaces are empty. Prefer richer/fresher rows.
  candidates.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  return candidates[0];
}

/**
 * Wipe every surface. Called when the user signs out, links to a real
 * (non-anonymous) account, or explicitly chooses "forget this device".
 */
export async function clearAnonCredentials() {
  try { localStorage.removeItem(LS_KEY); } catch (_) {}
  try {
    document.cookie = `${COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
  } catch (_) {}
  await _clearIdb();
  try { if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME); } catch (_) {}
}
